<template>
  <div
    ref="dropzoneRef"
    :class="[
      'tw-relative tw-transition-all tw-duration-200 tw-rounded-lg',
      compact
        ? 'tw-border tw-border-dashed tw-p-3'
        : 'tw-border-2 tw-border-dashed tw-p-6 tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center',
      isDragOver
        ? 'tw-border-blue-500 tw-bg-blue-50 dark:tw-bg-blue-950/20'
        : 'tw-border-neutral-300 dark:tw-border-neutral-700 tw-bg-neutral-50 dark:tw-bg-neutral-900/50',
      disabled
        ? 'tw-opacity-50 tw-cursor-not-allowed'
        : 'tw-cursor-pointer hover:tw-bg-neutral-100 dark:hover:tw-bg-neutral-800/50',
      hasError ? 'tw-border-red-500 tw-bg-red-50' : '',
    ]"
    @click="openFileDialog"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <input
      :id="inputId"
      ref="fileInputRef"
      type="file"
      :accept="accept"
      :disabled="disabled || remainingSlots <= 0"
      multiple
      class="tw-hidden"
      @change="handleFileSelect"
    />

    <div
      :class="
        compact ? 'tw-flex tw-items-center tw-gap-3 tw-w-full' : 'tw-text-center tw-space-y-2'
      "
    >
      <Upload
        :class="[
          compact ? 'tw-h-5 tw-w-5 tw-shrink-0' : 'tw-h-10 tw-w-10 tw-mx-auto',
          isDragOver ? 'tw-text-blue-600' : 'tw-text-neutral-400',
        ]"
      />
      <div :class="compact ? 'tw-min-w-0' : ''">
        <p class="tw-text-sm tw-font-medium tw-text-neutral-700 dark:tw-text-neutral-300">
          {{ uploadText || t('orders.documents.drag_drop_documents') }}
        </p>
        <p v-if="!compact" class="tw-text-xs tw-text-neutral-500 dark:tw-text-neutral-400">
          {{ description || t('orders.titles.doc_types') }}
        </p>
        <p v-if="remainingSlots < maxFiles" class="tw-text-xs tw-text-neutral-400">
          {{ t('orders.documents.slots_remaining', { count: remainingSlots }) }}
        </p>
      </div>
    </div>

    <p v-if="errorMessage" class="tw-text-xs tw-text-red-500 tw-mt-2">{{ errorMessage }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Upload } from 'lucide-vue-next';

interface Props {
  inputId?: string;
  accept?: string;
  maxSizeMb?: number;
  maxFiles?: number;
  currentCount?: number;
  disabled?: boolean;
  compact?: boolean;
  uploadText?: string;
  description?: string;
}

const props = withDefaults(defineProps<Props>(), {
  inputId: 'attachment-dropzone',
  accept: '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf',
  maxSizeMb: 20,
  // No cap by default — users can attach as many files as they need.
  maxFiles: Infinity,
  currentCount: 0,
  disabled: false,
  compact: false,
});

const emit = defineEmits<{
  add: [files: File[]];
  error: [message: string];
}>();

const { t } = useI18n();
const dropzoneRef = ref<HTMLElement>();
const fileInputRef = ref<HTMLInputElement>();
const isDragOver = ref(false);
const errorMessage = ref('');

const remainingSlots = computed(() => Math.max(0, props.maxFiles - props.currentCount));
const hasError = computed(() => !!errorMessage.value);

const openFileDialog = () => {
  if (!props.disabled && remainingSlots.value > 0 && fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const validateFile = (file: File): string | null => {
  if (file.size > props.maxSizeMb * 1024 * 1024) {
    return t('orders.documents.file_too_large', { size: props.maxSizeMb });
  }

  const acceptedTypes = props.accept.split(',').map(type => type.trim());
  const isValidType = acceptedTypes.some(type => {
    if (type.startsWith('.')) {
      return file.name.toLowerCase().endsWith(type.toLowerCase());
    }
    return file.type === type;
  });

  if (!isValidType) {
    return t('orders.documents.invalid_file_type');
  }

  return null;
};

const processFiles = (fileList: FileList | File[]) => {
  errorMessage.value = '';
  const incoming = Array.from(fileList);
  const allowed = incoming.slice(0, remainingSlots.value);

  if (incoming.length > remainingSlots.value) {
    errorMessage.value = t('orders.documents.max_files_reached', { max: props.maxFiles });
  }

  const validFiles: File[] = [];
  for (const file of allowed) {
    const validationError = validateFile(file);
    if (validationError) {
      errorMessage.value = validationError;
      emit('error', validationError);
      continue;
    }
    validFiles.push(file);
  }

  if (validFiles.length) {
    emit('add', validFiles);
  }

  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files?.length) {
    processFiles(target.files);
  }
};

const handleDrop = (event: DragEvent) => {
  isDragOver.value = false;
  if (props.disabled || !event.dataTransfer?.files?.length) return;
  processFiles(event.dataTransfer.files);
};

const handleDragOver = () => {
  if (!props.disabled && remainingSlots.value > 0) {
    isDragOver.value = true;
  }
};

const handleDragLeave = (event: DragEvent) => {
  const rect = dropzoneRef.value?.getBoundingClientRect();
  if (!rect) {
    isDragOver.value = false;
    return;
  }
  const { clientX, clientY } = event;
  if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
    isDragOver.value = false;
  }
};
</script>
